import {
  SerializedInventoryLocation,
  SerializedInventoryLocationBuilding,
  SerializedInventoryMeter,
} from '@enmon/commons/lib/tools';
import { PaginationByPage, Populate, buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import { SerializedCombinedMeter, SerializedFilter, SerializedMeter } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import { BaseApiClient } from './baseApiClient';
import { StrapiApiResponse } from '../../types/strapiResponse';

export interface FindInventoryLocationArgs {
  filter?: InventoryLocationsFilter;
  populate?: Populate<SerializedInventoryLocation>;
  signal?: AbortSignal;
}

export type FindInventoryLocationsResponse = StrapiApiResponse<SerializedInventoryLocation>;

export interface InventoryLocationsFilter {
  id?: SerializedInventoryLocation['id'];
  ids?: SerializedInventoryLocation['id'][];
}

export type GetLocationMetersFilter = Partial<PaginationByPage> &
  Partial<{
    apiKeys: SerializedMeter['apiKey'][];
    buildingFloors: SerializedMeter['buildingFloor'][];
    buildings: SerializedInventoryLocationBuilding['id'][];
    name: string;
    commodities: Commodity[];
    search: string;
    serial: string;
    sorts: SerializedFilter['sorts'];
    submittedAt: SerializedInventoryMeter['submitted_at'];
    submittedBy: SerializedInventoryMeter['submitted_by'];
  }>;

export type GetLocationMetersArg = {
  id?: SerializedInventoryLocation['id'];
  filter?: GetLocationMetersFilter;
  signal?: AbortSignal;
};

export type GetLocationMetersResponse = {
  count: number;
  result: SerializedCombinedMeter[];
};

export class InventoryLocationsApiClient extends BaseApiClient<StrapiApiResponse<SerializedInventoryLocation>> {
  constructor() {
    super('inventory-locations');
  }

  async find({
    filter = {},
    populate = {},
    signal,
  }: FindInventoryLocationArgs): Promise<FindInventoryLocationsResponse> {
    const { id, ids } = filter;

    return await this.http.get<FindInventoryLocationsResponse>('', {
      params: buildQuery<SerializedInventoryLocation>({
        sort: {
          name: 'asc',
        },
        filters: {
          ...(id ? { id: { $eq: id } } : {}),
          ...(ids ? { id: { $in: ids } } : {}),
        },
        populate,
      }),
      signal,
    });
  }

  async getLocationMeters({ id, filter, signal }: GetLocationMetersArg): Promise<GetLocationMetersResponse> {
    return await this.http.get<GetLocationMetersResponse>(`${id}/meters`, {
      params: filter,
      signal,
    });
  }

  async validateApiKey(
    apiKey: SerializedInventoryLocation['api_key'],
    signal: AbortSignal | undefined,
  ): Promise<Pick<SerializedInventoryLocation, 'id'>> {
    return await this.http.post(`/validate-api-key`, { apiKey }, { authenticator: null, signal });
  }
}
